import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Tag, Globe, AlertTriangle, ArrowRight, ChevronRight, Check, Lock, LockOpen } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtINR } from "@/lib/documents";

const D = (s) => { if (!s) return "—"; const d = new Date(s.length === 10 ? `${s}T00:00:00` : s); return isNaN(d) ? s.slice(0, 10) : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }); };
const DY = (s) => { if (!s) return "—"; const d = new Date(s.length === 10 ? `${s}T00:00:00` : s); return isNaN(d) ? s.slice(0, 10) : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); };
const T = (s) => { if (!s) return ""; const d = new Date(s); return isNaN(d) ? "" : d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }); };
const title = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const compactINR = (v) => (v >= 100000 ? `₹${(v / 100000).toFixed(2)}L` : `₹${Math.round(v || 0).toLocaleString("en-IN")}`);

const STAY_BADGE = { checked_out: "bg-[#EEF1F4] text-[#3A4A5A]", checked_in: "bg-[#DBFCEF] text-[#014751]", cancelled: "bg-[#FEE2E2] text-[#991B1B]", no_show: "bg-[#FEE2E2] text-[#991B1B]" };
const HK_DONE = new Set(["completed", "done", "inspected", "verified", "closed"]);
const hkBadge = (s) => (HK_DONE.has(s) ? ["DONE", "bg-[#DBFCEF] text-[#014751]"] : s === "in_progress" || s === "paused" ? ["IN PROGRESS", "bg-[#E6FEFF] text-[#014751]"] : [title(s).toUpperCase() || "PENDING", "bg-[#FEF3C7] text-[#78530E]"]);

function Badge({ cls, children, testid }) { return <span data-testid={testid} className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ${cls}`}>{children}</span>; }
function Empty({ icon: Icon, text }) { return <div className="py-10 text-center" data-testid="history-empty">{Icon && <Icon size={40} strokeWidth={1.25} color="#D5E6E8" className="mx-auto mb-2" />}<div className="text-[14px] text-[#97A6A9]">{text}</div></div>; }
function Row({ icon: Icon, children }) { return <div className="flex items-start gap-1.5 text-[12px] text-[#3F5257]"><Icon size={13} strokeWidth={1.5} color="#97A6A9" className="mt-0.5 shrink-0" /><span className="min-w-0">{children}</span></div>; }

const SUBTABS = [["stays", "Stays"], ["housekeeping", "Housekeeping"], ["maintenance", "Maintenance"], ["blocks", "Blocks"], ["notes", "Notes"], ["audit", "Audit"]];

export function RoomHistory({ roomId, data, range, onRangeChange, loading, onRefresh, onClose }) {
  const [tab, setTab] = useState("stays");
  const [draft, setDraft] = useState(range);
  const navigate = useNavigate();
  const go = (path) => { onClose?.(); navigate(path); };
  const s = data?.summary;

  return (
    <div data-testid="room-history">
      <div className="bg-[#F4FCFC] border-b border-[#E6EFF0] px-5 py-2.5 -mx-5 flex items-center gap-2" data-testid="history-range">
        <span className="text-[12px] text-[#3F5257] shrink-0">Period:</span>
        <div className="flex-1 min-w-0"><DatePicker value={draft.from} onChange={(v) => setDraft({ ...draft, from: v })} compact clearable={false} data-testid="history-from" /></div>
        <ArrowRight size={14} strokeWidth={1.5} color="#97A6A9" className="shrink-0" />
        <div className="flex-1 min-w-0"><DatePicker value={draft.to} onChange={(v) => setDraft({ ...draft, to: v })} compact clearable={false} data-testid="history-to" /></div>
        <button type="button" onClick={() => onRangeChange(draft)} disabled={loading || !draft.from || !draft.to || draft.from > draft.to}
          className="h-8 px-3 shrink-0 rounded-[6px] bg-[#37F4B5] text-[#014751] text-[12.5px] font-bold hover:bg-[#2CE3A6] disabled:opacity-50" data-testid="history-apply">Apply</button>
      </div>

      <div className="bg-[#E6FEFF] border-b border-[#E6EFF0] px-4 py-3 -mx-5 grid grid-cols-5" data-testid="history-summary">
        {[["Stays", s?.total_stays ?? "—", "total-stays"], ["Nights", s?.total_nights ?? "—", "total-nights"], ["Revenue", s ? compactINR(s.total_revenue) : "—", "total-revenue"],
          ["Issues", s?.total_issues ?? "—", "total-issues", (s?.total_issues || 0) > 0], ["Blocks", s?.total_blocks ?? "—", "total-blocks", false, true]].map(([l, v, tid, red, grey]) => (
          <div key={l} className="text-center min-w-0">
            <div className={`text-[18px] font-bold leading-tight truncate ${red ? "text-[#DC2626]" : grey ? "text-[#3A4A5A]" : "text-[#1A2425]"}`} data-testid={`history-${tid}`}>{v}</div>
            <div className="text-[11px] text-[#97A6A9]">{l}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-[#E6EFF0] -mx-5 px-3 no-scrollbar" data-testid="history-subtabs">
        {SUBTABS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`history-tab-${k}`}
            className={`shrink-0 px-3 py-2.5 text-[13px] font-semibold border-b-2 transition-colors ${tab === k ? "text-[#014751] border-[#37F4B5]" : "text-[#97A6A9] border-transparent hover:text-[#3F5257]"}`}>{l}</button>
        ))}
      </div>

      <div className="pt-3 -mx-5" data-testid={`history-panel-${tab}`}>
        {loading && <div className="px-5 space-y-2">{[0, 1, 2].map((i) => <div key={i} className="skeleton-shimmer h-20 rounded-lg" />)}</div>}
        {!loading && data && tab === "stays" && <Stays items={data.stays} go={go} />}
        {!loading && data && tab === "housekeeping" && <Housekeeping items={data.housekeeping} s={s} />}
        {!loading && data && tab === "maintenance" && <Maintenance items={data.maintenance} s={s} go={go} />}
        {!loading && data && tab === "blocks" && <Blocks items={data.blocks || []} s={s} />}
        {!loading && data && tab === "notes" && <Notes roomId={roomId} items={data.notes} onSaved={onRefresh} />}
        {!loading && data && tab === "audit" && <Audit timeline={data.timeline} audit={data.audit} />}
      </div>
    </div>
  );
}

function Stays({ items, go }) {
  if (!items.length) return <Empty icon={Calendar} text="No stays in this period" />;
  return (
    <div className="px-4 space-y-3 pb-3">
      {items.map((st) => (
        <div key={st.id} className="bg-white border border-[#D5E6E8] rounded-lg p-3.5" data-testid={`history-stay-${st.code}`}>
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <div className="text-[14px] font-bold text-[#1A2425] truncate">{st.guest_name}</div>
            <Badge cls={STAY_BADGE[st.status] || "bg-[#EEF1F4] text-[#3A4A5A]"}>{st.status === "checked_in" && <span className="w-1.5 h-1.5 rounded-full bg-[#14C48E]" />}{title(st.status)}</Badge>
          </div>
          <div className="space-y-1">
            <Row icon={Calendar}>{D(st.arrival)} → {D(st.departure)} · {st.nights} night{st.nights === 1 ? "" : "s"} · {st.num_adults || 0} Adult{st.num_adults === 1 ? "" : "s"}{st.num_children ? ` · ${st.num_children} Child${st.num_children === 1 ? "" : "ren"}` : ""}</Row>
            <Row icon={Tag}>{[st.rate_plan, st.amount ? fmtINR(st.amount) : null, st.payment_method ? title(st.payment_method === "upi" ? "UPI" : st.payment_method) : null].filter(Boolean).join(" · ") || "—"}</Row>
            {st.source && <Row icon={Globe}>{st.source}{st.source_ref ? ` (Ref: ${st.source_ref})` : ""}</Row>}
          </div>
          <div className="text-right mt-2">
            <button type="button" onClick={() => go(`/pms/stays/${st.id}`)} className="text-[12px] font-bold text-[#014751] hover:underline" data-testid={`history-view-stay-${st.code}`}>View Stay →</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Housekeeping({ items, s }) {
  return (
    <div>
      <div className="px-5 pb-2 text-[12px] text-[#3F5257]" data-testid="hk-summary">{s.hk_total} task{s.hk_total === 1 ? "" : "s"} · {s.hk_completed} completed · {s.hk_pending} pending</div>
      {!items.length ? <Empty text="No housekeeping tasks in this period" /> : items.map((t) => {
        const [label, cls] = hkBadge(t.state);
        return (
          <div key={t.id} className="flex items-start justify-between gap-3 border-b border-[#E6EFF0] px-5 py-3" data-testid={`history-hk-${t.id}`}>
            <div className="min-w-0">
              <div className="text-[13px] font-bold text-[#1A2425]">{D(t.created_at)} · {title(t.task_type)}</div>
              <div className="text-[12px] text-[#97A6A9]">{t.assigned_to_name || "Unassigned"}{t.completed_at ? ` · Completed ${T(t.completed_at)}` : t.started_at ? ` · Started ${T(t.started_at)}` : ""}</div>
              {t.notes && <div className="text-[12px] italic text-[#3F5257] mt-0.5">{t.notes}</div>}
            </div>
            <Badge cls={cls}>{label}</Badge>
          </div>
        );
      })}
    </div>
  );
}

function Maintenance({ items, s, go }) {
  return (
    <div>
      <div className="px-5 pb-2 text-[12px] text-[#3F5257]" data-testid="mt-summary">{s.mt_total} issue{s.mt_total === 1 ? "" : "s"} · {s.mt_resolved} resolved · {s.mt_open} open</div>
      {!items.length ? (
        <div className="mx-4 rounded-[6px] bg-[#DBFCEF] text-[#014751] text-[13px] text-center px-3 py-3 flex items-center justify-center gap-1.5" data-testid="history-empty">No maintenance issues in this period <Check size={14} strokeWidth={2} /></div>
      ) : (
        <div className="px-4 space-y-3 pb-3">
          {items.map((m) => (
            <div key={m.id} className="bg-white border border-[#D5E6E8] rounded-lg p-3.5" data-testid={`history-mt-${m.code || m.id}`}>
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-1.5 text-[13px] font-bold text-[#1A2425] min-w-0"><AlertTriangle size={14} strokeWidth={1.5} color="#F59E0B" className="shrink-0" /><span className="truncate">{m.title}</span></div>
                <Badge cls={m.is_resolved ? "bg-[#DBFCEF] text-[#014751]" : "bg-[#FEE2E2] text-[#991B1B]"}>{m.is_resolved ? "Resolved" : title(m.state) || "Open"}</Badge>
              </div>
              <div className="space-y-0.5 text-[12px] text-[#3F5257]">
                <div>Reported: {DY(m.reported_at)}{m.reported_by_name ? ` · by ${m.reported_by_name}` : m.source ? ` · by ${title(m.source)}` : ""}</div>
                {m.resolved_at && <div>Resolved: {DY(m.resolved_at)}{m.assigned_to_name ? ` · by ${m.assigned_to_name}` : ""}</div>}
                {m.out_of_order_days != null && <div className="text-[#97A6A9]">Out of order: {m.out_of_order_days} day{m.out_of_order_days === 1 ? "" : "s"}{m.out_of_order_days === 0 ? " (resolved same day)" : ""}</div>}
              </div>
              <div className="text-right mt-2">
                <button type="button" onClick={() => go(`/engineering?wo=${m.id}`)} className="text-[12px] font-bold text-[#014751] hover:underline" data-testid={`history-view-wo-${m.code || m.id}`}>View Details →</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Notes({ roomId, items, onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!text.trim()) { toast.error("Write a note first"); return; }
    setSaving(true);
    try { await api.post(`/rooms/${roomId}/notes`, { note: text.trim() }); toast.success("Note added"); setText(""); setOpen(false); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  return (
    <div>
      {open ? (
        <div className="mx-4 mb-3 rounded-lg border border-[#37F4B5] bg-white p-3" data-testid="note-editor">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} autoFocus placeholder="Add a note about this room..." maxLength={2000}
            className="w-full resize-none text-[13px] text-[#1A2425] outline-none placeholder:text-[#97A6A9]" data-testid="note-textarea" />
          <div className="flex justify-end gap-2 mt-2">
            <button type="button" onClick={() => { setOpen(false); setText(""); }} className="h-8 px-3 rounded-[6px] border border-[#D5E6E8] text-[12.5px] text-[#3F5257]" data-testid="note-cancel">Cancel</button>
            <button type="button" onClick={save} disabled={saving} className="h-8 px-3 rounded-[6px] bg-[#37F4B5] text-[#014751] text-[12.5px] font-bold disabled:opacity-50" data-testid="note-save">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setOpen(true)} className="mx-4 mb-3 h-11 w-[calc(100%-32px)] rounded-lg border border-dashed border-[#D5E6E8] bg-white text-[13px] text-[#97A6A9] hover:border-[#37F4B5]" data-testid="note-add">Add a note about this room...</button>
      )}
      {!items.length ? <Empty text="No notes yet — room notes persist across stays" /> : items.map((n) => (
        <div key={n.id} className="border-b border-[#E6EFF0] px-5 py-3" data-testid={`history-note-${n.id}`}>
          <div className="text-[12px] font-bold text-[#014751]">{D(n.created_at)} · {n.created_by_name || n.created_by_email}{n.created_by_role ? ` (${n.created_by_role})` : ""}</div>
          <div className="text-[13px] text-[#1A2425] leading-relaxed whitespace-pre-wrap">{n.note}</div>
        </div>
      ))}
    </div>
  );
}

function Audit({ timeline, audit }) {
  const rows = [...timeline.map((t) => ({ at: t.at, kind: "status", t })), ...audit.map((a) => ({ at: a.timestamp, kind: "audit", a }))].sort((x, y) => (y.at || "").localeCompare(x.at || ""));
  if (!rows.length) return <Empty text="No audit events in this period" />;
  return (
    <div className="px-5 pb-3">
      {rows.map((r, i) => (
        <div key={i} className="border-l-2 border-[#E6EFF0] pl-3 py-1.5" data-testid={`history-audit-${i}`}>
          <div className="text-[11px] mono text-[#97A6A9]">{(r.at || "").replace("T", " ").slice(0, 19)}</div>
          {r.kind === "status" ? (
            <div className="text-[13px] text-[#1A2425] flex items-center gap-1.5"><span className="mono text-[#3F5257]">{r.t.from_status}</span><ChevronRight size={12} strokeWidth={1.5} color="#97A6A9" /><span className="mono font-semibold">{r.t.to_status}</span>{r.t.note && <span className="text-[12px] text-[#3F5257]">· {r.t.note}</span>}</div>
          ) : (
            <div className="text-[13px] text-[#1A2425]"><span className="mono font-medium">{r.a.action}</span><span className="text-[12px] text-[#97A6A9]"> · {r.a.actor_email || "system"}</span></div>
          )}
        </div>
      ))}
    </div>
  );
}

const BLOCK_BADGE = { active: "bg-[#EEF1F4] border border-[#B7CACD] text-[#3A4A5A]", scheduled: "bg-[#E6FEFF] text-[#014751]", completed: "bg-[#DBFCEF] text-[#014751]", cancelled: "bg-[#FEE2E2] text-[#991B1B]" };
const DT = (s) => { if (!s) return "—"; const d = new Date(s); return isNaN(d) ? s : `${d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}, ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`; };

function Blocks({ items, s }) {
  return (
    <div>
      <div className="px-5 pb-2 text-[12px] text-[#3F5257]" data-testid="blocks-summary">{s.total_blocks} block{s.total_blocks === 1 ? "" : "s"} · {s.blocks_completed} completed · {s.blocks_active} active{s.blocks_scheduled ? ` · ${s.blocks_scheduled} scheduled` : ""}{s.blocks_cancelled ? ` · ${s.blocks_cancelled} cancelled` : ""}</div>
      {!items.length ? <Empty icon={LockOpen} text="No blocks recorded for this period" /> : items.map((b) => (
        <div key={b.id} className="flex items-start justify-between gap-3 border-b border-[#E6EFF0] px-5 py-3" data-testid={`history-block-${b.id}`}>
          <div className="min-w-0">
            <div className={`flex items-center gap-1.5 text-[13px] font-bold ${b.status === "cancelled" ? "text-[#97A6A9] line-through" : "text-[#1A2425]"}`}><Lock size={14} strokeWidth={1.5} color="#3A4A5A" />{D(b.block_from)} → {DY(b.block_to)} ({b.nights} night{b.nights === 1 ? "" : "s"})</div>
            <div className="text-[12px] text-[#3F5257]">{b.reason_label}{b.reason_note ? ` · ${b.reason_note}` : ""}</div>
            <div className="text-[11px] text-[#97A6A9]">Blocked by: {b.blocked_by_name || "—"} · {DT(b.blocked_at)}{b.status === "cancelled" && b.unblocked_at ? ` · Unblocked ${DT(b.unblocked_at)}` : ""}</div>
          </div>
          <Badge cls={BLOCK_BADGE[b.status] || BLOCK_BADGE.active}>{b.status}</Badge>
        </div>
      ))}
    </div>
  );
}
